import React, { useState } from "react";
import {
  Container,
  List,
  ListItem,
  Typography,
  IconButton,
  Divider,
  Box,
  Chip,
  Button,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import BgTitle from "components/title/BgTitle";
import { Link as RouterLink } from "react-router-dom";

const NewsMobileView = () => {
  const { t } = useTranslation();
  const newsItems = t("news", { returnObjects: true });
  const [showAll, setShowAll] = useState(false);

  // By default show only 3 items
  const visibleNewsItems = showAll ? newsItems : newsItems.slice(0, 3);
  const hasMoreItems = newsItems.length > 3;

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: 0,
        px: { xs: 0, sm: 2 }, // Remove padding on mobile for full-width feel
      }}
    >
      <Box
        sx={{
          p: "1rem",
          mx: 2,
        }}
      >
        <BgTitle text={t("news_headline")} />
      </Box>

      <List sx={{ mt: 3, mb: 3 }}>
        {visibleNewsItems.map((item, index) => {
          // Check if there's a valid link
          const hasValidLink =
            item.link && item.link !== "" && item.link !== "#";

          const isInternalLink = hasValidLink && item.link.startsWith("/");

          // Determine component and props based on link availability
          const listItemProps = hasValidLink
            ? {
                component: isInternalLink ? RouterLink : "a",
                ...(isInternalLink
                  ? { to: item.link }
                  : {
                      href: item.link,
                      target: "_blank",
                      rel: "noopener noreferrer",
                    }),
              }
            : {
                component: "div", // Use div instead of anchor when no link
              };

          return (
            <React.Fragment key={index}>
              <ListItem
                {...listItemProps}
                sx={{
                  py: 3,
                  px: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  textDecoration: "none",
                  color: "inherit",
                  bgcolor: "background.paper",
                  "&:active": {
                    bgcolor: hasValidLink ? "action.selected" : "inherit",
                  },
                  transition: "background-color 0.2s",
                  cursor: hasValidLink ? "pointer" : "default",
                }}
              >
                {/* Date Chip */}
                <Chip
                  label={item.date}
                  size="small"
                  sx={{
                    mb: 1,
                    bgcolor: item.important ? "red" : "primary.main",
                    color: "white", // This is correct conditional styling
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    height: 24,
                  }}
                />

                {/* News Title */}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 500,
                    lineHeight: 1.4,
                    fontSize: "0.8rem",
                    mb: 1,
                  }}
                >
                  {item.title}
                </Typography>

                {/* Link Icon - Only shown if there's a valid link */}
                {hasValidLink && (
                  <Box
                    sx={{
                      alignSelf: "flex-end",
                      mt: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      sx={{
                        color: "primary.main",
                        "&:active": {
                          bgcolor: "primary.light",
                        },
                      }}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </ListItem>
              {index < visibleNewsItems.length - 1 && (
                <Divider component="li" />
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* View More Button */}
      {hasMoreItems && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 4,
            px: 2,
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setShowAll(!showAll)}
            endIcon={
              <ExpandMoreIcon
                style={{ transform: showAll ? "rotate(180deg)" : "none" }}
              />
            }
            sx={{
              borderRadius: 6,
              width: "100%",
              py: 1,
              textTransform: "none",
              fontWeight: 500,
              borderColor: "rgba(12, 77, 150, 0.5)",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "rgba(12, 77, 150, 0.04)",
              },
            }}
          >
            {showAll
              ? t("company_history.show_less_btn")
              : t("company_history.show_more_btn")}
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default NewsMobileView;
